import React, { useState, useEffect, useCallback } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import Swal from 'sweetalert2'

const WarehouseStockLevels = () => {
  const [stocks, setStocks] = useState([])
  const [filteredStocks, setFilteredStocks] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [mainWarehouse, setMainWarehouse] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(null)
  const [stockValue, setStockValue] = useState(null)
  const [filters, setFilters] = useState({
    search: ''
  })

  const applyFilters = useCallback(() => {
    let filtered = [...stocks]

    // Search filter (product_name, product_sku)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(stock =>
        stock.product_name?.toLowerCase().includes(searchLower) ||
        stock.product_sku?.toLowerCase().includes(searchLower)
      )
    }

    setFilteredStocks(filtered)
  }, [stocks, filters])

  useEffect(() => {
    fetchWarehouses()
  }, [])

  useEffect(() => {
    if (mainWarehouse) {
      setSelectedWarehouseId(mainWarehouse.id)
    }
  }, [mainWarehouse])

  useEffect(() => {
    if (selectedWarehouseId) {
      fetchStockSummary(selectedWarehouseId)
      fetchStockValue(selectedWarehouseId)
    }
  }, [selectedWarehouseId])

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const fetchWarehouses = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.WAREHOUSES)
      const warehousesData = response.data.results || response.data || []
      setWarehouses(warehousesData)
      
      // Find main warehouse
      const main = warehousesData.find(w => w.is_main === true)
      if (main) {
        setMainWarehouse(main)
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || error.response?.data?.message || 'Failed to fetch warehouses. Please try again.',
        confirmButtonColor: '#d33'
      })
    }
  }

  const fetchStockSummary = async (warehouseId) => {
    try {
      setLoading(true)
      const params = { warehouse_id: warehouseId }

      const response = await apiClient.get('/api/stock/entries/', { params })
      const stocksData = response.data || []
      setStocks(stocksData)
      setFilteredStocks(stocksData)
    } catch (error) {
      console.error('Error fetching stock summary:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || error.response?.data?.message || 'Failed to fetch stock summary. Please try again.',
        confirmButtonColor: '#d33'
      })
      setStocks([])
      setFilteredStocks([])
    } finally {
      setLoading(false)
    }
  }

  const fetchStockValue = async (warehouseId) => {
    try {
      const params = { warehouse_id: warehouseId }
      
      const response = await apiClient.get('/api/stock/value/', { params })
      setStockValue(response.data)
    } catch (error) {
      console.error('Error fetching stock value:', error)
      // Don't show error toast for stock value, just set to null
      setStockValue(null)
    }
  }

  const handleWarehouseChange = (warehouseId) => {
    setSelectedWarehouseId(warehouseId)
  }

  const showStockDetails = (stock) => {
    const detailsHtml = `
      <div style="text-align: left; padding: 10px; background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); border-radius: 8px; margin-bottom: 10px;">
        <div style="display: flex; align-items: center; margin-bottom: 10px; padding-bottom: 10px; border-bottom: 2px solid rgba(0,0,0,0.1);">
          <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 16px; font-weight: bold; margin-right: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.2);">
            <i class="bi bi-box"></i>
          </div>
          <div>
            <h3 style="margin: 0; color: #2c3e50; font-weight: 600; font-size: 16px;">${stock.product_name || 'N/A'}</h3>
            <p style="margin: 2px 0 0 0; color: #7f8c8d; font-size: 12px;">Product ID: ${stock.product || 'N/A'}</p>
          </div>
        </div>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">SKU</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.product_sku || 'N/A'}</div>
          </div>
          
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Warehouse</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.warehouse_name || 'N/A'}</div>
          </div>
        </div>

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Supplier</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.supplier_name || 'N/A'}</div>
          </div>
          
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Quantity</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.quantity }</div>
          </div>
        </div>

        <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); margin-bottom: 8px;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 6px;">
            <div>
              <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Purchase Price</div>
              <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">$${stock.purchase_price || 0}</div>
            </div>
            <div>
              <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Total Cost</div>
              <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">$${stock.total_cost || 0}</div>
            </div>
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 6px;">
            <div>
              <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Batch Number</div>
              <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.batch_number || 'N/A'}</div>
            </div>
            <div>
              <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Original Batch</div>
              <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.original_batch_number || 'N/A'}</div>
            </div>
          </div>
          <div style="margin-bottom: 6px;">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Received Date</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.received_date ? new Date(stock.received_date).toLocaleString() : 'N/A'}</div>
          </div>
          <div style="margin-bottom: 6px;">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Reorder Level</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.reorder_level ?? 'N/A'}</div>
          </div>
          <div style="margin-bottom: 6px;">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Transfer Reference</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${stock.source_transfer_reference || 'N/A'}</div>
          </div>
          <div style="margin-bottom: 6px;">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Notes</div>
            <div style="color: #2c3e50; font-size: 12px; white-space: pre-wrap;">${stock.notes || 'N/A'}</div>
          </div>
          <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(0,0,0,0.05); font-size: 11px; color: #7f8c8d;">
            <div>Created By: <span style="color: #2c3e50;">${stock.created_by_email || 'N/A'}</span></div>
            <div>Created At: <span style="color: #2c3e50;">${stock.created_at ? new Date(stock.created_at).toLocaleString() : 'N/A'}</span></div>
          </div>
        </div>
      </div>
    `

    Swal.fire({
      title: '<span style="color: #2c3e50; font-size: 18px; font-weight: 600;">Stock Details</span>',
      html: detailsHtml,
      icon: 'info',
      showCloseButton: true,
      confirmButtonText: 'Close',
      confirmButtonColor: '#3085d6',
      width: '600px',
      heightAuto: false,
      padding: '1rem',
      customClass: {
        popup: 'swal2-popup-custom'
      }
    })
  }

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    })
  }

  const clearFilters = () => {
    setFilters({
      search: ''
    })
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Warehouse Stock Levels</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Home</li>
            <li className="breadcrumb-item">Product & Stock Verification</li>
            <li className="breadcrumb-item active">Warehouse Stock Levels</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ 
                  paddingBottom: '15px',
                  borderBottom: '2px solid #e9ecef'
                }}>
                  <div className="d-flex align-items-center gap-4 flex-wrap">
                    <h5 className="card-title mb-0">
                      <i className="bi bi-boxes me-2"></i>
                      Warehouse Stock Levels
                    </h5>
                    
                    {/* Stock Value Summary Badges */}
                    {stockValue && (
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div className="d-flex align-items-center" style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '600',
                          boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                        }}>
                          <i className="bi bi-box-seam me-2"></i>
                          <span>Quantity: <strong>{stockValue.total_quantity || 0}</strong></span>
                        </div>
                        
                        <div className="d-flex align-items-center" style={{
                          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '600',
                          boxShadow: '0 2px 8px rgba(17, 153, 142, 0.3)'
                        }}>
                          <i className="bi bi-currency-dollar me-2"></i>
                          <span>Value: <strong>${(stockValue.total_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                        </div>
                        
                        <div className="d-flex align-items-center" style={{
                          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '600',
                          boxShadow: '0 2px 8px rgba(245, 87, 108, 0.3)'
                        }}>
                          <i className="bi bi-graph-up me-2"></i>
                          <span>Avg Price: <strong>${(stockValue.average_purchase_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        if (selectedWarehouseId) {
                          fetchStockSummary(selectedWarehouseId)
                          fetchStockValue(selectedWarehouseId)
                        }
                      }}
                      disabled={loading}
                      style={{
                        borderRadius: '6px',
                        padding: '6px 14px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Refresh
                    </button>
                  </div>
                </div>
                <p className="text-muted">View and verify stock levels across all warehouses.</p>

                {/* Warehouse Selection */}
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 className="card-title mb-3">
                      <i className="bi bi-building me-2"></i>
                      Select Warehouse
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-12">
                        <label htmlFor="warehouse" className="form-label">Warehouse</label>
                        <select
                          className="form-select"
                          id="warehouse"
                          value={selectedWarehouseId || ''}
                          onChange={(e) => handleWarehouseChange(parseInt(e.target.value))}
                        >
                          {warehouses.map(warehouse => (
                            <option key={warehouse.id} value={warehouse.id}>
                              {warehouse.name} {warehouse.is_main ? '(Main)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Filters Section */}
                <div className="card mb-3">
                  <div className="card-body">
                   
                    <div className="row g-3">
                      <div className="col-md-10">
                        <label htmlFor="search" className="form-label">Search</label>
                        <input
                          type="text"
                          className="form-control"
                          id="search"
                          name="search"
                          placeholder="Product Name or SKU"
                          value={filters.search}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-md-2 d-flex align-items-end">
                        <button
                          className="btn btn-outline-secondary w-100"
                          onClick={clearFilters}
                          title="Clear filters"
                        >
                          <i className="bi bi-x-lg me-1"></i>
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stocks Table */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading stocks...</p>
                  </div>
                ) : filteredStocks.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                    <p className="mt-3 text-muted">No stocks found</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Product Name</th>
                          <th>SKU</th>
                          <th>Purchase Price</th>
                          <th> Quantity</th>
                          <th>Total Cost</th>
                          
                          <th style={{ width: '80px', textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStocks.map((stock, index) => (
                          <tr key={`${stock.product_id}-${index}`}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                                  <i className="bi bi-box"></i>
                                </div>
                                <div className="fw-semibold">{stock.product_name || 'N/A'}</div>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-secondary">{stock.product_sku || 'N/A'}</span>
                            </td>
                            <td>
                              <span className="text-muted">${stock.purchase_price || 0}</span>
                            </td>
                            <td>
                              <span className="fw-semibold">{stock.quantity || 0}</span>
                            </td>
                            <td>
                              <span className="fw-semibold text-success">${stock.total_cost || 0}</span>
                            </td>
                            
                            <td style={{ textAlign: 'center' }}>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => showStockDetails(stock)}
                                title="View Details"
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Results Count */}
                {!loading && (
                  <div className="mt-3 text-muted">
                    <small>
                      Showing <strong>{filteredStocks.length}</strong> of <strong>{stocks.length}</strong> products
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default WarehouseStockLevels

